import "../../css/Subscription.css";
import "../../css/GlobalStyles.css";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { Colors } from "../../utils/colors";
import {
  OnboardingFlowType,
  OVERFLOW_RATE,
  OVERFLOW_RATE_BUSINESS,
  OVERFLOW_RATE_PRO,
  PlanTypeV4,
  SubscriptionPlanType,
  ThemeType,
} from "@markit/common.types";
import { MARKIT_TEXTING_CREDITS } from "@markit/common.utils";
import SubscriptionPlanPrice from "../LandingPage/SubscriptionPlanPrice";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { ReactNode, useMemo } from "react";
import { useTheme } from "../../hooks/useTheme";
import { Icon } from "@iconify/react";
import TextHoverButton from "../Buttons/TextHoverButton";
import {
  SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
  SUBSCRIPTION_PLAN_CARD_MIN_WIDTH,
} from "../../utils/constants/globalConstants";
import OnboardingFlowDot from "../Onboarding/OnboardingFlowDot";

// Features that correlate to onboarding flows behind Markit+
export const ONBOARDING_FLOW_FEATURES = [
  OnboardingFlowType.PAID_MEMBERSHIP,
  OnboardingFlowType.INSTAGRAM_DM,
  OnboardingFlowType.IMPORT_CONTACTS,
  OnboardingFlowType.MCP_API,
];

type SubscriptionPlanCardProps = {
  cardButton: ReactNode;
  subscriptionPlan: SubscriptionPlanType;
  isSelected: boolean;
  selectOnPress?: () => void;
  homePage?: boolean; // dark mode
  fullWidth?: boolean;
  // Defined for Onboarding flow only - flow types the user selected on Customize Experience
  highlightedFlowTypes?: OnboardingFlowType[];
};

const SubscriptionPlanCard = (props: SubscriptionPlanCardProps) => {
  const {
    cardButton,
    subscriptionPlan,
    isSelected,
    selectOnPress,
    homePage,
    fullWidth,
    highlightedFlowTypes,
  } = props;
  const { theme } = useTheme();

  const styles = {
    bodyMediumBold: {
      fontWeight: 500,
      fontSize: 14,
    },
    labelSubtext: {
      fontSize: 12,
      fontWeight: 500,
      color: Colors.GRAY3,
    },
    sectionContainer: { gap: 10, paddingInline: 14 },
  };

  const renderFeatureLine = (
    text: ReactNode,
    flowType?: OnboardingFlowType
  ) => {
    const isHighlighted =
      !!flowType && !!highlightedFlowTypes?.includes(flowType);
    if (!isHighlighted) {
      return <span style={styles.bodyMediumBold}>{text}</span>;
    }
    return (
      <div className="AlignedRow" style={{ gap: 7 }}>
        <span style={{ ...styles.bodyMediumBold, color: Colors.BLUE5 }}>
          {text}
        </span>
        <OnboardingFlowDot flowTypes={[flowType]} />
      </div>
    );
  };

  const CardHorizontalDivider = useMemo(
    () => (
      <HorizontalDivider
        altMargin={14}
        eventTheme={homePage ? ThemeType.Dark : undefined}
      />
    ),
    [homePage]
  );

  const previousSubscriptionOption = useMemo(
    () =>
      subscriptionPlan.name === PlanTypeV4.BUSINESS.name
        ? PlanTypeV4.PRO
        : subscriptionPlan.name === PlanTypeV4.PRO.name
          ? PlanTypeV4.ESSENTIAL
          : PlanTypeV4.FREE,
    [subscriptionPlan.name]
  );

  const learnMoreAboutCredits = useMemo(
    () => (
      <TextHoverButton
        text="Learn more about credits"
        onPress={() => window.open(MARKIT_TEXTING_CREDITS)}
        icon={
          <Icon
            icon="ion:chevron-right"
            height={10}
            style={{ color: Colors.GRAY3, paddingTop: 1 }}
          />
        }
        iconPosition="right"
        textStyles={{ color: Colors.GRAY3 }}
      />
    ),
    []
  );

  const freeSubscriptionCard = (
    <>
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Texting</span>
        <div className="ColumnNormal" style={{ gap: 2 }}>
          <span style={styles.bodyMediumBold}>
            {PlanTypeV4.FREE.baselineLimit} Free texting credits / mo
          </span>
          {learnMoreAboutCredits}
        </div>
        <span style={styles.bodyMediumBold}>Shared sending number</span>
        <span style={styles.bodyMediumBold}>Basic texting automations</span>
        <span style={styles.bodyMediumBold}>Data segmentation</span>
        <span style={styles.bodyMediumBold}>Campaign analytics</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Monetization</span>
        <span style={styles.bodyMediumBold}>
          Digital products (video, audio, PDF)
        </span>
        <span style={styles.bodyMediumBold}>Custom newsletter/blog</span>
        <span style={styles.bodyMediumBold}>Ticketed events and webinars</span>
        <span style={styles.bodyMediumBold}>
          Apple Pay, Google Pay, Cashapp, etc.
        </span>
        <span style={styles.bodyMediumBold}>
          Standard platform & ticketing fees
        </span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Growth Features</span>
        <span style={styles.bodyMediumBold}>Link-in-bio</span>
        <span style={styles.bodyMediumBold}>Surveys & data capture forms</span>
        <span style={styles.bodyMediumBold}>Manual add contact</span>
      </div>
    </>
  );

  const basicSubscriptionCard = (
    <>
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Texting</span>
        <div className="ColumnNormal" style={{ gap: 2 }}>
          <span style={styles.bodyMediumBold}>
            {OVERFLOW_RATE} Cents per texting credit
          </span>
          {learnMoreAboutCredits}
        </div>
        <span style={styles.bodyMediumBold}>Dedicated Markit phone number</span>
        <span style={styles.bodyMediumBold}>Branded contact card</span>
        <span style={styles.bodyMediumBold}>Two-way messaging</span>
        <span style={styles.bodyMediumBold}>Inbox organization & filters</span>
        <span style={styles.bodyMediumBold}>Custom audience lists</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Monetization</span>
        {renderFeatureLine(
          "Paid subscriber memberships",
          OnboardingFlowType.PAID_MEMBERSHIP
        )}
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Growth Features</span>
        {renderFeatureLine(
          "Instagram automations",
          OnboardingFlowType.INSTAGRAM_DM
        )}
        <span style={styles.bodyMediumBold}>
          &quot;Text my number&quot; to sign up
        </span>
        <span style={styles.bodyMediumBold}>Sync phone contacts</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>More</span>
        {renderFeatureLine("AI tools (MCP) access", OnboardingFlowType.MCP_API)}
        <span style={styles.bodyMediumBold}>Human support</span>
        <span style={styles.bodyMediumBold}>
          Weekly Community Updates & Discord
        </span>
      </div>
    </>
  );

  const creatorSubscriptionCard = (
    <>
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Texting</span>
        <div className="ColumnNormal" style={{ gap: 2 }}>
          <span style={styles.bodyMediumBold}>
            {OVERFLOW_RATE_PRO} Cents per credit
          </span>
          {learnMoreAboutCredits}
        </div>
        <span style={styles.bodyMediumBold}>
          {PlanTypeV4.PRO.baselineLimit.toLocaleString()} Free credits / mo
        </span>
        <span style={styles.bodyMediumBold}>Text images and media</span>
        <span style={styles.bodyMediumBold}>AI-assistive messaging</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Monetization</span>
        <span style={styles.bodyMediumBold}>
          Affiliate and promoter rev sharing links
        </span>
        <span style={styles.bodyMediumBold}>
          0% Platform fee on paid subs & products
        </span>
        <span style={styles.bodyMediumBold}>Reduced event ticketing fees</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Growth Features</span>
        {renderFeatureLine("Import CSV", OnboardingFlowType.IMPORT_CONTACTS)}
        <span style={styles.bodyMediumBold}>Full integration access</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>More</span>
        <span style={styles.bodyMediumBold}>API & webhooks access</span>
        <span style={styles.bodyMediumBold}>Teams (3 seats)</span>
        <span style={styles.bodyMediumBold}>Priority human support</span>
      </div>
    </>
  );

  const businessSubscriptionCard = (
    <>
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Texting</span>
        <div className="ColumnNormal" style={{ gap: 2 }}>
          <span style={styles.bodyMediumBold}>
            {OVERFLOW_RATE_BUSINESS} Cents per credit
          </span>
          {learnMoreAboutCredits}
        </div>
        <span style={styles.bodyMediumBold}>
          {PlanTypeV4.BUSINESS.baselineLimit.toLocaleString()} Free credits / mo
        </span>
        <span style={styles.bodyMediumBold}>Custom phone number area code</span>
        <span style={styles.bodyMediumBold}>Faster send times</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>Monetization</span>
        <span style={styles.bodyMediumBold}>Lowest event ticketing fees</span>
      </div>
      {CardHorizontalDivider}
      <div className="ColumnNormal" style={styles.sectionContainer}>
        <span style={styles.labelSubtext}>More</span>
        <span style={styles.bodyMediumBold}>Teams (unlimited seats)</span>
        <span style={styles.bodyMediumBold}>White glove onboarding</span>
        <span style={styles.bodyMediumBold}>
          VIP human support / dedicated rep
        </span>
      </div>
    </>
  );

  return (
    <StandardBorderedContainer
      containerStyles={{
        minHeight: 800,
        ...(fullWidth
          ? { width: "100%", maxWidth: "100%" }
          : {
              minWidth: SUBSCRIPTION_PLAN_CARD_MIN_WIDTH,
              maxWidth: SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
              width: "100%",
            }),
        boxSizing: "border-box",
        gap: 14,
        paddingBlock: 14,
        borderRadius: 20,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: homePage ? Colors.GRAY10 : Colors.WHITE,
        border: isSelected
          ? `2px solid ${homePage ? Colors.WHITE : Colors.BLACK1}`
          : `1px solid ${
              homePage ? Colors.BLACK1 : theme.DividerColor.borderColor
            }`,
        cursor: selectOnPress ? "pointer" : "default",
      }}
      onPress={selectOnPress}
    >
      <div className="ColumnNormal">
        <div
          className="ColumnNormal"
          style={{
            alignItems: "center",
            height: 110,
            gap: 14,
            paddingBottom: 10,
            paddingInline: 14,
          }}
        >
          <div
            className="Centering"
            style={{
              height: 26,
              minHeight: 26,
              paddingInline: 7,
              borderRadius: 7,
              backgroundColor: homePage ? Colors.GRAY7 : Colors.GRAY6,
            }}
          >
            <span style={styles.bodyMediumBold}>
              {subscriptionPlan.name}
              {subscriptionPlan.name === PlanTypeV4.PRO.name ? (
                <span style={{ color: Colors.BLUE5 }}> (Most Popular)</span>
              ) : null}
            </span>
          </div>
          <SubscriptionPlanPrice
            priceInCents={subscriptionPlan.price}
            homePage={homePage}
          />
          <span style={{ fontSize: 12, fontWeight: 500, textAlign: "center" }}>
            {subscriptionPlan.name === PlanTypeV4.FREE.name
              ? "For testing and new creators."
              : subscriptionPlan.name === PlanTypeV4.ESSENTIAL.name
                ? "For creators and new businesses"
                : subscriptionPlan.name === PlanTypeV4.PRO.name
                  ? "For scaling creators and growing businesses"
                  : "For high-growth creators and scaling businesses"}
          </span>
        </div>
        {CardHorizontalDivider}
        {previousSubscriptionOption && subscriptionPlan.price > 0 ? (
          <>
            <div style={{ paddingInline: 14 }}>
              <span className="bodyBold">
                All features in{" "}
                <span style={{ color: Colors.BLUE5 }}>
                  {previousSubscriptionOption.name}
                </span>{" "}
                plus:
              </span>
            </div>
            {CardHorizontalDivider}
          </>
        ) : null}
        <div className="ColumnNormal">
          {subscriptionPlan.name === PlanTypeV4.FREE.name
            ? freeSubscriptionCard
            : subscriptionPlan.name === PlanTypeV4.ESSENTIAL.name
              ? basicSubscriptionCard
              : subscriptionPlan.name === PlanTypeV4.PRO.name
                ? creatorSubscriptionCard
                : businessSubscriptionCard}
        </div>
      </div>
      <div style={{ paddingInline: 14 }}>{cardButton}</div>
    </StandardBorderedContainer>
  );
};

export default SubscriptionPlanCard;
